import { useField, useFormikContext } from 'formik'
import { useState } from 'react'
import { Configure, Index, InstantSearch, Hits } from 'react-instantsearch'

import type { Hit, BaseHit } from 'instantsearch.js'
import type { ReactElement } from 'react'
import type { IIap } from '~/types/iap'
import type { IIapOption } from '~/types/option'
import type { IPageContainerFormValues, IPlaceholderRule } from '~/types/template'

import CustomHit from './CustomHit'
import IapInputFilters from './Filters'

import AlgoliaNoResultsBoundary from '~/components/Algolia/NoResultsBoundary'
import AlgoliaPagination from '~/components/Algolia/Pagination'
import AlgoliaSearch from '~/components/Algolia/Search'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import Label from '~/components/Label'
import { ModalBody, ModalHeader } from '~/components/Modal/Layout'
import { ALGOLIA_INDEX_NAMES } from '~/const/algolia'
import { DEFAULT_IAP_PRICE_COUNTRY } from '~/const/template'
import { getIapFilters, getSearchClient } from '~/utils/algolia'

import './styles.scss'

const searchClient = getSearchClient()

interface Props {
  name: string
  onItemClick: VoidFunction
  rules?: IPlaceholderRule[]
}

const IapInputModal = ({ name, onItemClick }: Props): ReactElement => {
  const [filters, setFilters] = useState({ products: true, subscriptions: true })

  const { values } = useFormikContext<IPageContainerFormValues>()

  const [, , { setValue }] = useField<IIapOption | undefined>(name)

  const configurationFilters =
    values.selectedApp &&
    getIapFilters({
      appKey: values.selectedApp?.app_key,
      deviceType: values.selectedApp?.device_type,
    })

  const handleItemClick = (option: IIapOption) => {
    setValue(option)
    onItemClick()
  }

  const handleFilterChanged = (key: 'products' | 'subscriptions') => {
    const newFilters = { ...filters, [key]: !filters[key] }
    setFilters(newFilters)
  }

  const transformItems = (items: (Hit<BaseHit> & IIap)[]) => {
    return items.map((item) => ({
      ...item,
      onClick: () =>
        handleItemClick({
          label: item.title,
          price: item.prices && item.prices[DEFAULT_IAP_PRICE_COUNTRY],
          status: item.status || '',
          value: item.sku,
        }),
    }))
  }

  return (
    <InstantSearch indexName={ALGOLIA_INDEX_NAMES.IAP_SUBSCRIPTIONS} searchClient={searchClient}>
      <Configure filters={configurationFilters} hitsPerPage={10} />
      <div className="iap-input-modal">
        <ModalHeader className="iap-input-modal__header">
          <div className="search-container">
            <AlgoliaSearch label="Search an in-app" placeholder="Search" wrapperClassName="search-container__input" />
            <div className="search-container__filters">
              <CheckboxInput
                defaultChecked
                label="Subscriptions"
                onChange={() => handleFilterChanged('subscriptions')}
              />
              <CheckboxInput defaultChecked label="Products" onChange={() => handleFilterChanged('products')} />
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="iap-input-modal__body">
          {filters.subscriptions && (
            <div className="results-container">
              <Index indexName={ALGOLIA_INDEX_NAMES.IAP_SUBSCRIPTIONS}>
                <Label className="results-container__label" label="Subscriptions" />
                <AlgoliaNoResultsBoundary fallback={<span className="no-iaps-message">No in-apps found.</span>}>
                  <div className="results-container__results">
                    <IapInputFilters />
                    <div className="results-container__results__table">
                      <Hits hitComponent={CustomHit} transformItems={transformItems} />
                      <AlgoliaPagination />
                    </div>
                  </div>
                </AlgoliaNoResultsBoundary>
              </Index>
            </div>
          )}
          {filters.products && (
            <div className="results-container">
              <Index indexName={ALGOLIA_INDEX_NAMES.IAP_PRODUCTS}>
                <Label className="results-container__label" label="Products" />
                <AlgoliaNoResultsBoundary fallback={<span className="no-iaps-message">No in-apps found.</span>}>
                  <div className="results-container__results">
                    <IapInputFilters />
                    <div className="results-container__results__table">
                      <Hits hitComponent={CustomHit} transformItems={transformItems} />
                      <AlgoliaPagination />
                    </div>
                  </div>
                </AlgoliaNoResultsBoundary>
              </Index>
            </div>
          )}
        </ModalBody>
      </div>
    </InstantSearch>
  )
}

export default IapInputModal
